/* eslint-disable better-mutation/no-mutation */

import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getSelectedShippingProducts } from './shipping-selectors';
import { validate } from './validate';
import { v4 as uuidv4 } from 'uuid';
import type {
    ProductsPerWeightSpan,
    ShippingOption,
    ShippingProduct
} from 'tradera/state/services/types/webapi-selling-generated';

const getUuid = () => uuidv4();

type ShippingState = {
    validationErrors: Record<string, string>;
    globalValidationError: string | null;
    shippingByWeight: ProductsPerWeightSpan[] | undefined;
    shippingRecommendations: {
        shippingProductId: number;
        shippingProvider: string;
        weight: number;
    }[];
    recommendedWeight: number | null;
    selectedShippingOptions: ShippingProduct[];
    shippingOptionsLoading: boolean;
    shippingOptionsError: boolean;
    offerTakeaway: boolean;
    offerShipping: boolean;
    fromCountry: string | null;
};

const initialState: ShippingState = validate({
    validationErrors: {},
    globalValidationError: null,
    shippingByWeight: [],
    shippingRecommendations: [],
    recommendedWeight: null,
    selectedShippingOptions: [],
    shippingOptionsLoading: false,
    shippingOptionsError: false,
    offerTakeaway: false,
    offerShipping: false,
    fromCountry: null
});

const shippingContainsTakeaway = (shippingOptions: ShippingOption[]) =>
    shippingOptions.some(
        (shippingOption) =>
            shippingOption.type === 'Takeaway' ||
            shippingOption.type === 'TakeawayOnly'
    );

const shippingContainsProduct = (shippingOptions: ShippingOption[]) =>
    shippingOptions.some(
        (shippingOption) =>
            shippingOption.type !== 'Takeaway' &&
            shippingOption.type !== 'TakeawayOnly'
    );

const shippingSlice = createSlice({
    name: 'shipping',
    initialState,
    reducers: {
        resetSelectedShippingOptions: (state) => {
            state.selectedShippingOptions = [];
            state.offerShipping = false;
            state.offerTakeaway = false;
            return validate(state);
        },
        setShippingOptionsByWeight: (
            state,
            action: PayloadAction<{
                productsPerWeightSpan:
                    | ProductsPerWeightSpan[]
                    | null
                    | undefined;
                shippingOptions: ShippingOption[];
            }>
        ) => {
            const { productsPerWeightSpan, shippingOptions } = action.payload;

            state.offerShipping = shippingContainsProduct(shippingOptions);
            state.offerTakeaway = shippingContainsTakeaway(shippingOptions);

            //Decorate products with cost, type & productId for draft api:
            const productsDecorated = productsPerWeightSpan?.map(
                (weightClass) => {
                    const productsWithDecoration = weightClass.products?.map(
                        (product) => ({
                            ...product,
                            cost: product.price,
                            productId: product.id,
                            type: product.providerId,
                            uuid: getUuid()
                        })
                    );
                    return { ...weightClass, products: productsWithDecoration };
                }
            );

            state.shippingByWeight = productsDecorated;
            state.selectedShippingOptions = getSelectedShippingProducts(
                productsDecorated,
                shippingOptions,
                state.offerTakeaway
            );
            state.shippingOptionsLoading = false;
            state.shippingOptionsError = false;
            return validate(state);
        },
        setShippingOptionSelected: (
            state,
            action: PayloadAction<ShippingProduct[]>
        ) => {
            const { payload } = action;

            const selectedOptions = getSelectedShippingProducts(
                state.shippingByWeight,
                payload,
                state.offerTakeaway
            );

            if (shippingContainsProduct(selectedOptions)) {
                state.offerShipping = true;
            } else {
                state.offerShipping = false;
            }

            state.selectedShippingOptions = selectedOptions;

            return validate(state);
        },
        setTakeawaySelected: (
            state,
            action: PayloadAction<{ offerTakeaway: boolean }>
        ) => {
            state.offerTakeaway = action.payload.offerTakeaway;
            state.selectedShippingOptions = getSelectedShippingProducts(
                state.shippingByWeight,
                state.selectedShippingOptions,
                state.offerTakeaway
            );

            return validate(state);
        },
        setShippingOptionsError: (state) => {
            state.shippingOptionsLoading = false;
            state.shippingOptionsError = true;

            return validate(state);
        },
        setShippingFromCountry: (
            state,
            action: PayloadAction<{ fromCountry: string }>
        ) => {
            state.fromCountry = action.payload.fromCountry;
        },
        clearShippingRecommendations: (state) => {
            state.recommendedWeight = initialState.recommendedWeight;
            state.shippingRecommendations =
                initialState.shippingRecommendations;
        },
        setShippingRecommendations: (
            state,
            action: PayloadAction<{
                recommendedWeight: number;
                shippingRecommendations: {
                    shippingProductId: number;
                    shippingProvider: string;
                    weight: number;
                }[];
            }>
        ) => {
            const { recommendedWeight, shippingRecommendations } =
                action.payload;

            state.recommendedWeight = recommendedWeight;
            state.shippingRecommendations = shippingRecommendations;
            return validate(state);
        }
    }
});

export const {
    setShippingFromCountry,
    setShippingOptionsByWeight,
    setShippingOptionsError,
    setShippingRecommendations,
    clearShippingRecommendations,
    setTakeawaySelected,
    setShippingOptionSelected,
    resetSelectedShippingOptions
} = shippingSlice.actions;

export default shippingSlice.reducer;
